/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import AuthRequired from '@/utils/auth-required'
import { checkServerVersion, scheduleVersionReload } from "@/utils";
import { supportState, supportActive, endSupport, setSupportReturnPath } from '@/utils/student-support-session'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      redirect: '/',
      beforeEnter: AuthRequired,
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          name: 'home',
          component: () => import('./views/pages/Home/index.vue'),
        },
        {
          path: '/redirect',
          name: 'redirect',
          component: () => import('./views/pages/Redirect.vue'),
        },
        {
          path: '/paymentAndCheck/payment',
          name: 'paymentAndCheck-payment',
          component: () => import('./views/pages/PaymentAndCheck/Payment'),
          meta: {
            breadcrumb: [
              // { title: 'Home', url: '/' },
              // { title: { i18n: 'sections.accountInfomation.title', default: 'Admin' } }
              { title: 'Home', url: '/' },
              { title: { i18n: 'sections.paymentAndCheck.title', default: 'Payment And Check' } },
              { title: { i18n: 'sections.paymentAndCheck.payment.title', default: 'Payment' }, url: '/paymentAndCheck/payment', active: true },
            ],
            pageTitle: { i18n: 'sections.paymentAndCheck.title', default: 'Payment And Check' }
          }
        },
        {
          path: '/paymentAndCheck/history',
          name: 'paymentAndCheck-history',
          component: () => import('./views/pages/PaymentAndCheck/History'),
          meta: {
            breadcrumb: [
              // { title: 'Home', url: '/' },
              // { title: { i18n: 'sections.accountInfomation.title', default: 'Admin' } }
              { title: 'Home', url: '/' },
              { title: { i18n: 'sections.paymentAndCheck.title', default: 'Payment And Check' } },
              { title: { i18n: 'sections.paymentAndCheck.history.title', default: 'History' }, url: '/paymentAndCheck/history', active: true },
            ],
            pageTitle: { i18n: 'sections.paymentAndCheck.title', default: 'Payment And Check' }
          }
        },
        {
          path: '/paymentAndCheck/statementHistory',
          name: 'paymentAndCheck-statementHistory',
          component: () => import('./views/pages/PaymentAndCheck/StatementHistory'),
          meta: {
            breadcrumb: [
              // { title: 'Home', url: '/' },
              // { title: { i18n: 'sections.accountInfomation.title', default: 'Admin' } }
              { title: 'Home', url: '/' },
              { title: { i18n: 'sections.paymentAndCheck.title', default: 'Payment And Check' } },
              { title: { i18n: 'sections.paymentAndCheck.statementHistory.title', default: 'Statement History' }, url: '/paymentAndCheck/statementHistory', active: true },
            ],
            pageTitle: { i18n: 'sections.paymentAndCheck.title', default: 'Account Infomation' }
          }
        },
        {
          path: '/accountInfomation',
          name: 'accountInfomation',
          component: () => import('./views/pages/AccountInformation/index.vue'),
          meta: {
            breadcrumb: [
              // { title: 'Home', url: '/' },
              // { title: { i18n: 'sections.accountInfomation.title', default: 'Admin' } }
              { title: 'Home', url: '/' },
              { title: { i18n: 'sections.accountInfomation.title', default: 'Account Infomation' } },
              { title: { i18n: 'sections.accountInfomation.info.title', default: 'Account Infomation' }, url: '/accountInfomation', active: true },
            ],
            pageTitle: { i18n: 'sections.accountInfomation.title', default: 'Account Infomation' }
          }
        },
        {
          path: '/logout',
          name: 'logout',
          component: () => import('./views/pages/Logout'),
        },
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/user',
          name: 'user',
          component: () => import('@/views/user'),
          redirect: '/user/login',
          children: [
            {
              path: 'login',
              name: 'user-login',
              component: () => import('@/views/user/Login')
            },
            {
              path: 'forgot-password',
              name: 'user-forgot-password',
              component: () => import('@/views/user/ForgotPassword')
            },
            {
              path: 'reset-password',
              name: 'user-reset-password',
              component: () => import('@/views/user/ResetPassword'),
              beforeEnter: AuthRequired
            }
          ]
        },
        {
          path: '/student-support',
          name: 'student-support',
          component: () => import('@/views/pages/StudentSupport.vue')
        },
        {
          path: '/authentication/:token/:username/:id',
          name: 'authentication',
          component: () => import('@/views/pages/Authentication.vue')
        },
        {
          path: '/resetKey/:resetKey',
          name: 'resetKey',
          component: () => import('@/views/pages/ResetKey.vue')
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue')
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue')
        }
      ]
    },
    // External Link From Admin App
    // Redirect to 404 page, if no match found
    // {
    //   path: '*',
    //   redirect: '/pages/error-404'
    // }
  ]
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

// eslint-disable-line no-unused-vars
router.beforeEach(async (from, to, next) => {
  // Existing callback names are reversed: `from` is the destination route here.
  if (supportState.mode) {
    if (from.path === '/logout') { endSupport('ออกจากโหมดผู้ดูแลแล้ว สามารถปิดแท็บนี้ได้'); next(false); return }
    if (!['/', '/student-support', '/accountInfomation', '/paymentAndCheck/payment', '/paymentAndCheck/history', '/paymentAndCheck/statementHistory'].includes(from.path)) { next(false); return }
    if (from.path !== '/student-support' && !supportActive()) { next('/student-support'); return }
    if (from.path !== '/student-support' && !supportState.ready) {
      setSupportReturnPath(from.path)
      next('/student-support')
      return
    }
  }
  //reload if not the same version as server
  const correctVersion = await checkServerVersion();
  if(correctVersion){
    next();
  }else{
    scheduleVersionReload();
  }
});
export default router
